import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import PaymentIcon from '@mui/icons-material/Payment'
import LinkIcon from '@mui/icons-material/Link'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import SupportAgentIcon from '@mui/icons-material/SupportAgent'
import EventAvailableIcon from '@mui/icons-material/EventAvailable'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import BusinessIcon from '@mui/icons-material/Business'
import PsychologyIcon from '@mui/icons-material/Psychology'
import MedicalServicesIcon from '@mui/icons-material/MedicalServices'
import AssignmentIcon from '@mui/icons-material/Assignment'
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import { getSupportPaymentLinks, sendSupportPaymentLink } from 'src/supportPortal/services/paymentLinksService'
import { usePatientReferenceData } from 'src/supportPortal/views/patients/hooks/usePatientReferenceData'
import { getPriceStringWithCurrency } from 'src/mentor/helpers/utilityFunctions'
import { displayNamesOfEventTypes, SESSION_PLATFORM_OFFLINE, SESSION_PLATFORM_ONLINE } from 'src/consumer/constants'

dayjs.extend(utc)

const CATEGORY_CONFIG = {
  events: {
    label: 'Events',
    icon: <EventAvailableIcon sx={{ fontSize: 18 }} />,
  },
  packs: {
    label: 'Packs',
    icon: <Inventory2OutlinedIcon sx={{ fontSize: 18 }} />,
  },
  other: {
    label: 'Other',
    icon: <MoreHorizIcon sx={{ fontSize: 18 }} />,
  },
}

const PAGE_SIZE = 20

const STATUS_STYLES = {
  Active: { bg: alpha('#2563eb', 0.12), color: '#2563eb' },
  Pending: { bg: alpha('#f59e0b', 0.14), color: '#b45309' },
  Paid: { bg: alpha('#10b981', 0.14), color: '#047857' },
  Expired: { bg: alpha('#ef4444', 0.14), color: '#b91c1c' },
  Cancelled: { bg: alpha('#64748b', 0.14), color: '#475569' },
}

const STATUS_FILTER_OPTIONS = ['Active', 'Pending', 'Paid', 'Expired', 'Cancelled']

const EVENT_TYPE_META = {
  consultation: { label: displayNamesOfEventTypes?.consultation || 'Consultation', color: '#2563eb', icon: <MedicalServicesIcon sx={{ fontSize: 16 }} /> },
  'nutrition-consultation': { label: displayNamesOfEventTypes?.['nutrition-consultation'] || 'Nutrition Consultation', color: '#0f766e', icon: <MedicalServicesIcon sx={{ fontSize: 16 }} /> },
  'therapy-session': { label: displayNamesOfEventTypes?.['therapy-session'] || 'Therapy Session', color: '#7c3aed', icon: <PsychologyIcon sx={{ fontSize: 16 }} /> },
  screening: { label: displayNamesOfEventTypes?.screening || 'Screening', color: '#db2777', icon: <AssignmentIcon sx={{ fontSize: 16 }} /> },
  assessment: { label: displayNamesOfEventTypes?.assessment || 'Assessment', color: '#875e8a', icon: <AssignmentIcon sx={{ fontSize: 16 }} /> },
  mixed: { label: 'Mixed Event Types', color: '#475569', icon: <MoreHorizIcon sx={{ fontSize: 16 }} /> },
  other: { label: 'Other', color: '#475569', icon: <MoreHorizIcon sx={{ fontSize: 16 }} /> },
  unknown: { label: 'Unknown', color: '#475569', icon: <MoreHorizIcon sx={{ fontSize: 16 }} /> },
}

function getCategoryLabel(category) {
  return CATEGORY_CONFIG?.[category]?.label || category
}

function getModeLabel(mode) {
  if (mode === SESSION_PLATFORM_ONLINE) {
    return 'Video call (Aumhum)'
  }
  if (mode === SESSION_PLATFORM_OFFLINE) {
    return 'In-clinic'
  }
  if (mode === 'mixed') {
    return 'Mixed mode'
  }
  return mode || null
}

function getEventGroupMeta(group) {
  return EVENT_TYPE_META[group] || {
    label: displayNamesOfEventTypes?.[group] || group || 'Other',
    color: '#475569',
    icon: <MoreHorizIcon sx={{ fontSize: 16 }} />,
  }
}

function formatDateTime(value) {
  if (!value) return '-'
  return dayjs.utc(value).local().format('DD MMM YYYY, hh:mm A')
}

function formatRelativeExpiry(value, pastPrefix, futurePrefix) {
  if (!value) {
    return 'No expiry'
  }
  const target = dayjs.utc(value)
  const now = dayjs()
  const days = Math.abs(target.diff(now, 'day'))
  if (target.isBefore(now)) {
    return `${pastPrefix} ${target.local().format('DD MMM YYYY')} (${days} day${days === 1 ? '' : 's'} ago)`
  }
  if (days >= 30) {
    const months = Math.max(1, Math.round(days / 30))
    return `${futurePrefix} ${target.local().format('DD MMM YYYY')} (in ${months} month${months === 1 ? '' : 's'})`
  }
  return `${futurePrefix} ${target.local().format('DD MMM YYYY')} (in ${days} day${days === 1 ? '' : 's'})`
}

function ChannelIcon({ channel }) {
  if (channel === 'mail') {
    return <MailOutlineIcon sx={{ fontSize: 18 }} />
  }
  if (channel === 'whatsapp') {
    return <WhatsAppIcon sx={{ fontSize: 18 }} />
  }
  return <LinkIcon sx={{ fontSize: 18 }} />
}

function PaymentLinkSendDialog({ open, onClose, row, onSent }) {
  const [customEmail, setCustomEmail] = useState('')
  const [customWhatsapp, setCustomWhatsapp] = useState('')
  const [sendingPayload, setSendingPayload] = useState(null)

  useEffect(() => {
    if (!open) {
      setCustomEmail('')
      setCustomWhatsapp('')
      setSendingPayload(null)
    }
  }, [open])

  const canSend = row?.canSend === true

  const handleSend = async (payload) => {
    if (!row?.id || !payload?.channel) {
      return
    }
    setSendingPayload(payload)
    const result = await sendSupportPaymentLink({
      paymentLinkId: row.id,
      ...payload,
    })
    if (result?.ok) {
      toast.success('Payment link sent')
      onSent?.()
      return
    }
    toast.error(result?.message || 'Could not send payment link')
  }

  const existingContacts = Array.isArray(row?.contactOptions) ? row.contactOptions : []
  const nonHumanContacts = existingContacts.filter((item) => item?.destinationType !== 'human-support')
  const humanSupportContact = existingContacts.find((item) => item?.destinationType === 'human-support')

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack spacing={0.75}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Send Payment Link
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {row?.itemTitle || 'Payment link'}
          </Typography>
          {row?.paymentLink ? (
            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
              {row.paymentLink}
            </Typography>
          ) : null}
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {!canSend ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            {row?.accessSummary || 'This role is view-only for this payment link.'}
          </Alert>
        ) : null}

        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Patient Contacts
            </Typography>
            <Stack spacing={1}>
              {nonHumanContacts.length > 0 ? (
                nonHumanContacts.map((contact) => (
                  <Paper
                    key={contact?.id}
                    variant="outlined"
                    sx={{
                      p: 1.25,
                      borderRadius: 2,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Stack spacing={0.25}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {contact?.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {contact?.displayValue}
                      </Typography>
                    </Stack>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ChannelIcon channel={contact?.channel} />}
                      disabled={!canSend || sendingPayload?.id === contact?.id}
                      onClick={() =>
                        handleSend({
                          id: contact?.id,
                          channel: contact?.channel,
                          destinationType: contact?.destinationType,
                          value: contact?.value,
                        })
                      }
                    >
                      {sendingPayload?.id === contact?.id ? 'Sending...' : `Send via ${contact?.channel === 'mail' ? 'Email' : 'WhatsApp'}`}
                    </Button>
                  </Paper>
                ))
              ) : (
                <Alert severity="info">No patient contacts were resolved for this payment link.</Alert>
              )}
            </Stack>
          </Box>

          <Divider />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Stack spacing={1}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Custom Email
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="name@example.com"
                  value={customEmail}
                  onChange={(event) => setCustomEmail(event.target.value)}
                />
                <Button
                  variant="outlined"
                  startIcon={<MailOutlineIcon />}
                  disabled={!canSend || !customEmail.trim() || sendingPayload?.id === 'custom-email'}
                  onClick={() =>
                    handleSend({
                      id: 'custom-email',
                      channel: 'mail',
                      destinationType: 'custom-email',
                      value: customEmail.trim(),
                    })
                  }
                >
                  {sendingPayload?.id === 'custom-email' ? 'Sending...' : 'Send Email'}
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Stack spacing={1}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Custom WhatsApp
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="+91..."
                  value={customWhatsapp}
                  onChange={(event) => setCustomWhatsapp(event.target.value)}
                />
                <Button
                  variant="outlined"
                  startIcon={<WhatsAppIcon />}
                  disabled={!canSend || !customWhatsapp.trim() || sendingPayload?.id === 'custom-whatsapp'}
                  onClick={() =>
                    handleSend({
                      id: 'custom-whatsapp',
                      channel: 'whatsapp',
                      destinationType: 'custom-whatsapp',
                      value: customWhatsapp.trim(),
                    })
                  }
                >
                  {sendingPayload?.id === 'custom-whatsapp' ? 'Sending...' : 'Send WhatsApp'}
                </Button>
              </Stack>
            </Grid>
          </Grid>

          <Divider />

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Support Routing
            </Typography>
            <Button
              variant="outlined"
              startIcon={<SupportAgentIcon />}
              disabled={!canSend || !humanSupportContact?.value || sendingPayload?.id === 'human-support'}
              onClick={() =>
                handleSend({
                  id: 'human-support',
                  channel: 'whatsapp',
                  destinationType: 'human-support',
                  value: humanSupportContact?.value,
                })
              }
            >
              {sendingPayload?.id === 'human-support' ? 'Sending...' : 'Send To Human Support'}
            </Button>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

function PaymentLinkCard({ row, onSend }) {
  const eventMeta = getEventGroupMeta(row?.eventTypeGroup)
  const statusStyle = STATUS_STYLES?.[row?.statusLabel] || STATUS_STYLES.Active

  const handleCopy = async () => {
    if (!row?.paymentLink) return
    await navigator.clipboard.writeText(row.paymentLink)
    toast.success('Payment link copied')
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 3,
        p: 2,
        borderColor: alpha(eventMeta.color, 0.28),
        background: `linear-gradient(135deg, ${alpha(eventMeta.color, 0.08)} 0%, rgba(255,255,255,0.98) 100%)`,
      }}
    >
      <Stack spacing={1.5}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} justifyContent="space-between">
          <Stack spacing={0.75} sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                icon={eventMeta.icon}
                label={eventMeta.label}
                sx={{
                  backgroundColor: alpha(eventMeta.color, 0.14),
                  color: eventMeta.color,
                  fontWeight: 700,
                }}
              />
              <Chip
                size="small"
                label={row?.statusLabel || 'Unknown'}
                sx={{
                  backgroundColor: statusStyle.bg,
                  color: statusStyle.color,
                  fontWeight: 700,
                }}
              />
              {row?.viewOnly ? (
                <Chip
                  size="small"
                  icon={<LockOutlinedIcon sx={{ fontSize: 14 }} />}
                  label="View Only"
                  variant="outlined"
                />
              ) : null}
            </Stack>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {row?.itemTitle || 'Payment link'}
            </Typography>
            {row?.itemSubtitle ? (
              <Typography variant="body2" color="text.secondary">
                {row.itemSubtitle}
              </Typography>
            ) : null}
            {row?.itemSummary ? (
              <Typography variant="caption" color="text.secondary">
                {row.itemSummary}
              </Typography>
            ) : null}
          </Stack>

          <Stack spacing={0.75} alignItems={{ xs: 'flex-start', md: 'flex-end' }}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
              {getPriceStringWithCurrency(row?.amount, row?.currency, true)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Created {formatDateTime(row?.paymentLinkCreatedTime || row?.createdTime)}
            </Typography>
          </Stack>
        </Stack>

        <Grid container spacing={1.25}>
          <Grid item xs={12} md={6}>
            <Stack spacing={0.75}>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <PersonOutlineIcon sx={{ fontSize: 16, color: '#64748b' }} />
                <Typography variant="body2">
                  {row?.patientName || 'Unknown patient'}
                  {row?.patientNumber ? ` • #${row.patientNumber}` : ''}
                </Typography>
              </Stack>
              {row?.practitionerName ? (
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <PsychologyIcon sx={{ fontSize: 16, color: '#64748b' }} />
                  <Typography variant="body2">{row.practitionerName}</Typography>
                </Stack>
              ) : null}
              {(row?.clinicBrandName || row?.clinicLocationName) ? (
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <BusinessIcon sx={{ fontSize: 16, color: '#64748b' }} />
                  <Typography variant="body2">
                    {[row?.clinicBrandName, row?.clinicLocationName].filter(Boolean).join(' • ')}
                  </Typography>
                </Stack>
              ) : null}
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack spacing={0.75}>
              {(row?.durationInMinutes || row?.modeLabel) ? (
                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                  {row?.durationInMinutes ? (
                    <Chip size="small" icon={<AccessTimeIcon sx={{ fontSize: 15 }} />} label={`${row.durationInMinutes} mins`} />
                  ) : null}
                  {row?.modeLabel ? (
                    <Chip
                      size="small"
                      icon={row?.mode === SESSION_PLATFORM_ONLINE ? <VideocamOutlinedIcon sx={{ fontSize: 15 }} /> : <LocationOnOutlinedIcon sx={{ fontSize: 15 }} />}
                      label={getModeLabel(row.mode)}
                    />
                  ) : null}
                </Stack>
              ) : null}

              {row?.category === 'packs' ? (
                <Stack spacing={0.5}>
                  {row?.usageLabel ? (
                    <Typography variant="body2" color="text.secondary">
                      {row.usageLabel}
                    </Typography>
                  ) : null}
                  {row?.bundlePackExpiresAt ? (
                    <Typography variant="body2" color="text.secondary">
                      {formatRelativeExpiry(row.bundlePackExpiresAt, 'Expired on', 'Expires on')}
                    </Typography>
                  ) : null}
                </Stack>
              ) : (
                row?.paymentLinkExpiredTime ? (
                  <Typography variant="body2" color="text.secondary">
                    {formatRelativeExpiry(row.paymentLinkExpiredTime, 'Expired on', 'Expires on')}
                  </Typography>
                ) : null
              )}
            </Stack>
          </Grid>
        </Grid>

        {row?.paymentLink ? (
          <Paper
            variant="outlined"
            sx={{
              p: 1,
              borderRadius: 2,
              backgroundColor: '#fff',
              borderColor: alpha('#94a3b8', 0.3),
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <LinkIcon sx={{ color: '#475569' }} />
              <Typography variant="body2" sx={{ flex: 1, wordBreak: 'break-all' }}>
                {row.paymentLink}
              </Typography>
              <Tooltip title="Copy link">
                <IconButton size="small" onClick={handleCopy}>
                  <ContentCopyIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Open link">
                <IconButton
                  size="small"
                  onClick={() => window.open(row.paymentLink, '_blank', 'noopener,noreferrer')}
                >
                  <OpenInNewIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Paper>
        ) : (
          <Alert severity="warning">No payment URL is currently available on this record.</Alert>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            {row?.accessSummary || ' '}
          </Typography>
          <Button
            variant="contained"
            startIcon={<PaymentIcon />}
            disabled={row?.canSend !== true}
            onClick={() => onSend?.(row)}
            sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
          >
            {row?.canSend ? 'Send Payment Link' : 'View Only'}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  )
}

export default function SupportPaymentLinksPage() {
  const [loading, setLoading] = useState(true)
  const [items, setItems] = useState([])
  const [countsByCategory, setCountsByCategory] = useState({})
  const [selectedCategory, setSelectedCategory] = useState('events')
  const [selectedMentorId, setSelectedMentorId] = useState('')
  const [selectedStatusLabel, setSelectedStatusLabel] = useState('')
  const [selectedClinicBrandId, setSelectedClinicBrandId] = useState('')
  const [selectedClinicLocationId, setSelectedClinicLocationId] = useState('')
  const [patientSearchText, setPatientSearchText] = useState('')
  const [debouncedPatientSearchText, setDebouncedPatientSearchText] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(PAGE_SIZE)
  const [totalItems, setTotalItems] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [reloadNonce, setReloadNonce] = useState(0)
  const [selectedGroupFilters, setSelectedGroupFilters] = useState({
    events: [],
    packs: [],
    other: [],
  })
  const [selectedRow, setSelectedRow] = useState(null)
  const {
    mentors,
    clinicBrands,
    clinicLocations,
    loading: referenceLoading,
    fetchReferenceData,
  } = usePatientReferenceData()

  const activeGroups = selectedGroupFilters[selectedCategory] || []

  useEffect(() => {
    fetchReferenceData()
  }, [fetchReferenceData])

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedPatientSearchText(patientSearchText.trim())
    }, 300)
    return () => window.clearTimeout(timer)
  }, [patientSearchText])

  useEffect(() => {
    let active = true

    const loadData = async () => {
      setLoading(true)
      const result = await getSupportPaymentLinks({
        page,
        limit: PAGE_SIZE,
        category: selectedCategory,
        ...(selectedMentorId ? { 'mentor-id': selectedMentorId } : {}),
        ...(selectedStatusLabel ? { 'status-label': selectedStatusLabel } : {}),
        ...(selectedClinicBrandId ? { 'clinic-brand-id': selectedClinicBrandId } : {}),
        ...(selectedClinicLocationId ? { 'clinic-location-id': selectedClinicLocationId } : {}),
        ...(debouncedPatientSearchText ? { 'patient-search': debouncedPatientSearchText } : {}),
        ...(selectedCategory === 'events' && activeGroups.length > 0
          ? { 'event-type-group': activeGroups.join(',') }
          : {}),
      })

      if (!active) {
        return
      }

      if (result?.ok) {
        setItems(result.items || [])
        setCountsByCategory(result.countsByCategory || {})
        setPageSize(result.pageSize || PAGE_SIZE)
        setTotalItems(result.totalItems || 0)
        setTotalPages(result.totalPages || 0)
      } else {
        toast.error(result?.message || 'Could not load payment links')
      }
      setLoading(false)
    }

    loadData()

    return () => {
      active = false
    }
  }, [
    activeGroups,
    debouncedPatientSearchText,
    page,
    reloadNonce,
    selectedCategory,
    selectedClinicBrandId,
    selectedClinicLocationId,
    selectedMentorId,
    selectedStatusLabel,
  ])

  const availableGroups = useMemo(() => {
    return [...new Set(items.map((item) => item?.eventTypeGroup).filter(Boolean))]
  }, [items])

  const mentorOptions = useMemo(() => {
    return (Array.isArray(mentors) ? mentors : [])
      .filter((item) => item?.id && (item?.name || item?.title))
      .map((item) => ({ id: item.id, label: item.name || item.title }))
      .sort((left, right) => left.label.localeCompare(right.label))
  }, [mentors])

  const statusOptions = useMemo(() => {
    return STATUS_FILTER_OPTIONS
  }, [])

  const clinicBrandOptions = useMemo(() => {
    return (Array.isArray(clinicBrands) ? clinicBrands : [])
      .filter((item) => item?.id && (item?.brandName || item?.title))
      .map((item) => ({ id: item.id, label: item.brandName || item.title }))
      .sort((left, right) => left.label.localeCompare(right.label))
  }, [clinicBrands])

  const clinicLocationOptions = useMemo(() => {
    return (Array.isArray(clinicLocations) ? clinicLocations : [])
      .filter((item) => {
        if (!item?.id || !item?.title) {
          return false
        }
        const locationBrandId = item?.clinicBrandId || item?.brandData?.id || item?.clinicBrand?.id
        if (selectedClinicBrandId && locationBrandId !== selectedClinicBrandId) {
          return false
        }
        return true
      })
      .map((item) => ({ id: item.id, label: item.title }))
      .sort((left, right) => left.label.localeCompare(right.label))
  }, [clinicLocations, selectedClinicBrandId])

  const filteredItems = useMemo(() => {
    if (!activeGroups.length) {
      return items
    }
    return items.filter((item) => activeGroups.includes(item?.eventTypeGroup))
  }, [activeGroups, items])

  const pageCount = Math.max(1, totalPages || 1)

  useEffect(() => {
    setPage(1)
  }, [
    debouncedPatientSearchText,
    selectedCategory,
    selectedClinicBrandId,
    selectedClinicLocationId,
    selectedMentorId,
    selectedStatusLabel,
    selectedGroupFilters,
  ])

  useEffect(() => {
    if (page > pageCount) {
      setPage(pageCount)
    }
  }, [page, pageCount])

  useEffect(() => {
    if (
      selectedMentorId &&
      !mentorOptions.some((option) => option.id === selectedMentorId)
    ) {
      setSelectedMentorId('')
    }
  }, [mentorOptions, selectedMentorId])

  useEffect(() => {
    if (
      selectedStatusLabel &&
      !statusOptions.includes(selectedStatusLabel)
    ) {
      setSelectedStatusLabel('')
    }
  }, [selectedStatusLabel, statusOptions])

  useEffect(() => {
    if (
      selectedClinicBrandId &&
      !clinicBrandOptions.some((option) => option.id === selectedClinicBrandId)
    ) {
      setSelectedClinicBrandId('')
    }
  }, [clinicBrandOptions, selectedClinicBrandId])

  useEffect(() => {
    if (
      selectedClinicLocationId &&
      !clinicLocationOptions.some((option) => option.id === selectedClinicLocationId)
    ) {
      setSelectedClinicLocationId('')
    }
  }, [clinicLocationOptions, selectedClinicLocationId])

  const groupedItems = useMemo(() => {
    return filteredItems.reduce((acc, item) => {
      const key = item?.eventTypeGroup || 'other'
      if (!acc[key]) {
        acc[key] = []
      }
      acc[key].push(item)
      return acc
    }, {})
  }, [filteredItems])

  const handleToggleGroup = (group) => {
    setSelectedGroupFilters((prev) => {
      const existing = prev[selectedCategory] || []
      const next = existing.includes(group)
        ? existing.filter((item) => item !== group)
        : [...existing, group]
      return {
        ...prev,
        [selectedCategory]: next,
      }
    })
  }

  return (
    <>
      <SupportHeader heading="Payment Links" showBackButton />
      <Box sx={{ maxWidth: 1480, mx: 'auto', p: { xs: 2, md: 3 } }}>
        <Stack spacing={2.5}>
          <Paper
            variant="outlined"
            sx={{
              p: 2.25,
              borderRadius: 3,
              background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.08) 0%, rgba(255,255,255,0.98) 100%)',
            }}
          >
            <Stack spacing={1.25}>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>
                Support Payment Links
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Events, packs, and other payment links are listed together here with role-aware visibility. Therapy Support L2 can view therapy-related links, Therapy Support L3 can send them, and clinic success roles can act on consultation links within their authorized clinic scope.
              </Typography>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ borderRadius: 3 }}>
            <Tabs
              value={selectedCategory}
              onChange={(_, value) => setSelectedCategory(value)}
              sx={{ px: 2, borderBottom: '1px solid rgba(148, 163, 184, 0.2)' }}
            >
              {Object.entries(CATEGORY_CONFIG).map(([category, meta]) => (
                <Tab
                  key={category}
                  value={category}
                  label={`${meta.label} (${countsByCategory?.[category] || 0})`}
                  icon={meta.icon}
                  iconPosition="start"
                  sx={{ textTransform: 'none', minHeight: 56 }}
                />
              ))}
            </Tabs>

            <Box sx={{ p: 2 }}>
              <Stack spacing={1.5} sx={{ mb: 2 }}>
                <Grid container spacing={1.25}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Patient Number / Name"
                      placeholder="Search patient"
                      value={patientSearchText}
                      onChange={(event) => setPatientSearchText(event.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.25}>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      label="Mentor"
                      value={selectedMentorId}
                      onChange={(event) => setSelectedMentorId(event.target.value)}
                    >
                      <MenuItem value="">All mentors</MenuItem>
                      {mentorOptions.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={1.75}>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      label="Status"
                      value={selectedStatusLabel}
                      onChange={(event) => setSelectedStatusLabel(event.target.value)}
                    >
                      <MenuItem value="">All statuses</MenuItem>
                      {statusOptions.map((status) => (
                        <MenuItem key={status} value={status}>
                          {status}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.5}>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      label="Clinic Brand"
                      value={selectedClinicBrandId}
                      onChange={(event) => {
                        setSelectedClinicBrandId(event.target.value)
                        setSelectedClinicLocationId('')
                      }}
                    >
                      <MenuItem value="">All clinic brands</MenuItem>
                      {clinicBrandOptions.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.5}>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      label="Clinic Location"
                      value={selectedClinicLocationId}
                      onChange={(event) => setSelectedClinicLocationId(event.target.value)}
                    >
                      <MenuItem value="">All clinic locations</MenuItem>
                      {clinicLocationOptions.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>

                {(patientSearchText || selectedMentorId || selectedStatusLabel || selectedClinicBrandId || selectedClinicLocationId) ? (
                  <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="caption" color="text.secondary">
                      {activeGroups.length > 0 && selectedCategory !== 'events'
                        ? `${filteredItems.length} matching payment link${filteredItems.length === 1 ? '' : 's'} on this page`
                        : `${totalItems} matching payment link${totalItems === 1 ? '' : 's'}`}
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => {
                        setPatientSearchText('')
                        setSelectedMentorId('')
                        setSelectedStatusLabel('')
                        setSelectedClinicBrandId('')
                        setSelectedClinicLocationId('')
                      }}
                    >
                      Clear filters
                    </Button>
                  </Stack>
                ) : null}
              </Stack>

              {selectedCategory !== 'other' && availableGroups.length > 0 ? (
                <Stack spacing={1.25} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Filter by Event Type Group
                  </Typography>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {availableGroups.map((group) => {
                      const meta = getEventGroupMeta(group)
                      const isActive = activeGroups.includes(group)
                      return (
                        <Chip
                          key={group}
                          label={meta.label}
                          onClick={() => handleToggleGroup(group)}
                          variant={isActive ? 'filled' : 'outlined'}
                          sx={{
                            backgroundColor: isActive ? alpha(meta.color, 0.14) : undefined,
                            borderColor: alpha(meta.color, 0.35),
                            color: meta.color,
                            fontWeight: 700,
                          }}
                        />
                      )
                    })}
                    {activeGroups.length > 0 ? (
                      <Chip label="Clear filters" onClick={() => setSelectedGroupFilters((prev) => ({ ...prev, [selectedCategory]: [] }))} />
                    ) : null}
                  </Stack>
                </Stack>
              ) : null}

              {(loading || referenceLoading) ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                  <CircularProgress />
                </Box>
              ) : filteredItems.length === 0 ? (
                <Alert severity="info">
                  No {getCategoryLabel(selectedCategory).toLowerCase()} payment links match the current filter.
                </Alert>
              ) : (
                <Stack spacing={2}>
                  {Object.entries(groupedItems).map(([group, groupRows]) => {
                    const meta = getEventGroupMeta(group)
                    return (
                      <Box key={group}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
                          <Chip
                            icon={meta.icon}
                            label={`${meta.label} (${groupRows.length})`}
                            sx={{
                              backgroundColor: alpha(meta.color, 0.14),
                              color: meta.color,
                              fontWeight: 700,
                            }}
                          />
                        </Stack>
                        <Stack spacing={1.5}>
                          {groupRows.map((row) => (
                            <PaymentLinkCard key={row?.id} row={row} onSend={setSelectedRow} />
                          ))}
                        </Stack>
                      </Box>
                    )
                  })}

                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    sx={{ pt: 1 }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {totalItems > 0
                        ? `Showing ${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, totalItems)} of ${totalItems}`
                        : 'No payment links found'}
                    </Typography>
                    <Pagination
                      page={page}
                      count={pageCount}
                      color="primary"
                      onChange={(_, value) => setPage(value)}
                    />
                  </Stack>
                </Stack>
              )}
            </Box>
          </Paper>
        </Stack>
      </Box>

      <PaymentLinkSendDialog
        open={!!selectedRow}
        row={selectedRow}
        onClose={() => setSelectedRow(null)}
        onSent={() => {
          setSelectedRow(null)
          setReloadNonce((value) => value + 1)
        }}
      />
    </>
  )
}
